import { yupResolver } from '@hookform/resolvers/yup';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import TextInput from '~components/FormFields/v2/TextInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

interface ReportScanIssueDialogProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
}

const customInstructionsSchema = yup.object().shape({
  customInstructions: yup.string().optional().default(''),
});

type CustomInstructionsDTO = yup.InferType<typeof customInstructionsSchema>;

const reportIssueSchema = yup.object().shape({
  description: yup
    .string()
    .trim()
    .min(1, t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple')),
});

type ReportIssueDTO = yup.InferType<typeof reportIssueSchema>;

interface CustomInstructionsViewProps {
  control: Control<CustomInstructionsDTO>;
  isSaving: boolean;
  onReportIssueClick: () => void;
}

function CustomInstructionsView({
  control,
  isSaving,
  onReportIssueClick,
}: CustomInstructionsViewProps) {
  return (
    <>
      <Typography variant="h6">
        {t('ticket_drawer_v2.details.ai_ticket_scanning_instructions_heading')}
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
        {t('ticket_drawer_v2.details.ai_ticket_scanning_instructions_subcopy')}
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        {t(
          'administration.configuration.settings.ai_ticket_scanning.custom_instructions_title',
        )}
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('ticket_drawer_v2.details.ai_ticket_scanning_instructions_example')}
      </Typography>

      <TextInput
        control={control}
        isDisabled={isSaving}
        name="customInstructions"
        rows={4}
        sx={{ mt: 1 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
        <OutlinedFlagIcon sx={{ fontSize: 16, color: 'info.main' }} />

        <PseudoLink
          action={onReportIssueClick}
          title={t('ticket_drawer_v2.details.report_scanning_issue_link')}
        />
      </Box>
    </>
  );
}

interface IssueReportViewProps {
  canEditInstructions: boolean;
  control: Control<ReportIssueDTO>;
  onAddInstructionsClick: () => void;
}

function IssueReportView({
  canEditInstructions,
  control,
  onAddInstructionsClick,
}: IssueReportViewProps) {
  return (
    <>
      <Typography variant="h6">
        {t('ticket_drawer_v2.details.report_scanning_issue_heading')}
      </Typography>

      {!canEditInstructions && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('ticket_drawer_v2.details.report_scanning_issue_no_permission')}
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        {t('ticket_drawer_v2.details.report_scanning_issue_field_label')}
      </Typography>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('ticket_drawer_v2.details.report_scanning_issue_example')}
      </Typography>

      <TextInput control={control} name="description" rows={3} sx={{ mt: 1 }} />

      {canEditInstructions && (
        <PseudoLink
          action={onAddInstructionsClick}
          sx={{ mt: 2 }}
          title={t('ticket_drawer_v2.details.add_custom_instructions')}
        />
      )}
    </>
  );
}

export default function ReportScanIssueDialog({
  isOpen,
  onClose,
  ticketId,
}: ReportScanIssueDialogProps) {
  const { toasterStore, userStore } = useStores();
  const { updateCompany } = useCompany();
  const [isSaving, setIsSaving] = useState(false);
  const [isReportingIssue, setIsReportingIssue] = useState(false);

  const userPermissions = userStore.getPermissions();
  const canEditInstructions = userPermissions.canEditCompany;
  const persistedCustomInstructions =
    userStore.userCompany.aiTicketScanningCustomInstructions ?? '';

  const {
    control: instructionsControl,
    formState: { isDirty: areInstructionsEdited },
    handleSubmit: handleInstructionsSubmit,
    reset: resetInstructions,
  } = useForm<CustomInstructionsDTO>({
    resolver: yupResolver(customInstructionsSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // The persisted value is loaded by the open-effect below, not here.
    defaultValues: { customInstructions: '' },
  });

  const {
    control: reportControl,
    formState: { isDirty: isDescriptionEntered, isSubmitting: isReporting },
    handleSubmit: handleReportSubmit,
    reset: resetReport,
  } = useForm<ReportIssueDTO>({
    resolver: yupResolver(reportIssueSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { description: '' },
  });

  // The ticket drawer keeps this dialog mounted between openings, so both forms and the
  // starting view are re-established every time it opens rather than only at mount.
  // Users who cannot edit the instructions have nothing to do in that view, so they open
  // straight into reporting.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    resetInstructions({ customInstructions: persistedCustomInstructions });
    resetReport({ description: '' });
    setIsReportingIssue(!canEditInstructions);
  }, [
    canEditInstructions,
    isOpen,
    persistedCustomInstructions,
    resetInstructions,
    resetReport,
  ]);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  const showCustomInstructions = useCallback(() => {
    setIsReportingIssue(false);
  }, []);

  const showIssueReport = useCallback(() => {
    setIsReportingIssue(true);
  }, []);

  const saveCustomInstructions = handleInstructionsSubmit(
    async ({ customInstructions }) => {
      setIsSaving(true);

      try {
        await updateCompany({
          company: {
            id: userStore.userCompany.id,
            aiTicketScanningCustomInstructions: customInstructions || null,
          },
        });

        toasterStore.push(
          alert(
            t('ticket_drawer_v2.details.ai_ticket_scanning_instructions_saved'),
            AlertTypes.success,
          ),
        );
        onClose();
      } catch {
        // Keep the dialog open with the entered text so the user can retry
      } finally {
        setIsSaving(false);
      }
    },
  );

  const reportIssue = handleReportSubmit(({ description }) => {
    Sentry.captureMessage('TicketScanningFailure', {
      level: 'log',
      extra: {
        description,
        ticket_id: ticketId,
        timestamp: new Date().toISOString(),
      },
    });

    toasterStore.push(
      alert(
        t('ticket_drawer_v2.details.report_scanning_issue_success_message'),
        AlertTypes.success,
      ),
    );
    onClose();
  });

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} preventCloseOnBackdropClick>
      <DialogHeader onClose={handleClose}>
        {t('administration.configuration.settings.ai_ticket_scanning.setting_title')}
      </DialogHeader>

      {/*
        The two views MUST stay separate components rather than inline JSX branches. Each
        renders a TextInput at the same position in the tree, so React reuses a single
        component instance across the switch and its `useController` keeps the previous
        view's `control` — the field then silently stops recording what the user types.
      */}
      <DialogContent>
        {isReportingIssue ? (
          <IssueReportView
            canEditInstructions={canEditInstructions}
            control={reportControl}
            onAddInstructionsClick={showCustomInstructions}
          />
        ) : (
          <CustomInstructionsView
            control={instructionsControl}
            isSaving={isSaving}
            onReportIssueClick={showIssueReport}
          />
        )}
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSaving}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        {isReportingIssue ? (
          <SmallButton
            disabled={!isDescriptionEntered || isReporting}
            onClick={reportIssue}
          >
            {t('actions.report_issue')}
          </SmallButton>
        ) : (
          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={!areInstructionsEdited}
            loading={isSaving}
            onClick={saveCustomInstructions}
          >
            {t('actions.save_instructions')}
          </SmallLoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
